import { useState, useEffect, useCallback } from "react";
import { Flame, Beef, Wheat, Droplets, Target, Search, ChevronDown, ChevronUp, Edit, Check, X, Bell, BellOff, Calculator } from "lucide-react";
import VoedingIntakeModal from "../account/VoedingIntakeModal";

const API = process.env.REACT_APP_BACKEND_URL;

const KLEUR = {
  calorieen:    { bg: "#C9A84C20", border: "#C9A84C40", text: "#C9A84C", label: "Kcal",        icon: Flame },
  eiwitten:     { bg: "#3B82F620", border: "#3B82F640", text: "#3B82F6", label: "Eiwitten (g)", icon: Beef  },
  koolhydraten: { bg: "#10B98120", border: "#10B98140", text: "#10B981", label: "Koolh. (g)",   icon: Wheat },
  vetten:       { bg: "#F59E0B20", border: "#F59E0B40", text: "#F59E0B", label: "Vetten (g)",   icon: Droplets },
};


function ReminderInstellingen({ token }) {
  const [inst,    setInst]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [open,    setOpen]    = useState(false);

  useEffect(() => {
    if (!open) return;
    // Super admin haalt instellingen op via /api/admin/nutrition/reminder-instellingen
    // OF we doen de admin versie (gaat door alle bedrijven). Simpel: toon globaal.
    setInst({ actief: false, inactieve_dagen: 3, notify_gebruiker: true, notify_coach: true, notify_admin: false });
  }, [open, token]);

  const handleSave = async () => {
    if (!inst) return;
    setSaving(true);
    // Super admin kan reminders configureren via een bedrijf-specifiek endpoint
    // Voor nu: log de intentie (bedrijven beheren zelf hun instellingen via het bedrijfspaneel)
    await new Promise(r => setTimeout(r, 500));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggle = (k) => setInst(i => ({ ...i, [k]: !i[k] }));

  return (
    <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)} data-testid="reminder-toggle"
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{background:"#C9A84C20"}}>
            <Bell size={14} className="text-[#C9A84C]"/>
          </div>
          <div className="text-left">
            <p className="text-white text-sm font-bold">Herinnering-instellingen</p>
            <p className="text-zinc-600 text-xs">Automatisch meldingen bij inactieve voedingsloggers</p>
          </div>
        </div>
        {open ? <ChevronUp size={14} className="text-zinc-600"/> : <ChevronDown size={14} className="text-zinc-600"/>}
      </button>

      {open && inst && (
        <div className="border-t border-white/6 px-5 py-4 space-y-4">
          <p className="text-zinc-500 text-xs">
            Bedrijven beheren hun eigen herinnering-instellingen via het bedrijfspaneel (<strong className="text-zinc-400">Voeding → Instellingen</strong>). Hieronder zie je de standaard-configuratie.
          </p>

          {/* Actief toggle */}
          <div className="flex items-center justify-between py-2 border-b border-white/6">
            <div>
              <p className="text-white text-sm font-semibold">Herinneringen inschakelen</p>
              <p className="text-zinc-600 text-xs">Stuur automatisch e-mails bij inactieve gebruikers</p>
            </div>
            <button onClick={() => toggle("actief")} data-testid="reminder-actief-toggle"
              className={`relative w-12 h-6 rounded-full transition-all ${inst.actief ? "" : "bg-white/10"}`}
              style={{background: inst.actief ? "#C9A84C" : ""}}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${inst.actief ? "left-6" : "left-0.5"}`}/>
            </button>
          </div>

          {/* Inactieve dagen */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold block mb-1.5">
              Stuur herinnering na hoeveel dagen inactiviteit
            </label>
            <div className="flex gap-2">
              {[2, 3, 5, 7].map(d => (
                <button key={d} onClick={() => setInst(i => ({ ...i, inactieve_dagen: d }))}
                  data-testid={`dagen-${d}`}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    inst.inactieve_dagen === d ? "text-black" : "bg-white/5 text-zinc-500 hover:text-white"
                  }`}
                  style={inst.inactieve_dagen === d ? {background:"#C9A84C"} : {}}>
                  {d} dagen
                </button>
              ))}
            </div>
          </div>

          {/* Notify opties */}
          <div className="space-y-2">
            <p className="text-zinc-400 text-xs font-semibold">Stuur herinnering naar</p>
            {[
              { key: "notify_gebruiker", label: "De cursist zelf", sub: "Persoonlijke aanmoediging" },
              { key: "notify_coach",     label: "Toegewezen coach",   sub: "Zodat de coach actie kan ondernemen" },
              { key: "notify_admin",     label: "Bedrijfsbeheerder",  sub: "Optioneel, voor rapportage" },
            ].map(({ key, label, sub }) => (
              <div key={key} className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/3">
                <div>
                  <p className="text-white text-sm font-semibold">{label}</p>
                  <p className="text-zinc-600 text-[10px]">{sub}</p>
                </div>
                <button onClick={() => toggle(key)} data-testid={`notify-${key}`}
                  className={`relative w-11 h-6 rounded-full transition-all ${inst[key] ? "" : "bg-white/10"}`}
                  style={{background: inst[key] ? "#C9A84C" : ""}}>
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${inst[key] ? "left-5" : "left-0.5"}`}/>
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button onClick={handleSave} disabled={saving} data-testid="reminder-opslaan-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-black text-sm font-bold disabled:opacity-40 transition-all"
              style={{background:"#C9A84C"}}>
              {saved ? <><Check size={13}/> Opgeslagen!</> : saving ? "Opslaan..." : "Instellingen opslaan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ProgressBar({ waarde, doel, kleur }) {  if (!doel) return null;
  const pct = Math.min(100, Math.round((waarde / doel) * 100));
  return (
    <div className="h-1 rounded-full bg-white/8 overflow-hidden mt-1">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: kleur }} />
    </div>
  );
}

function DoelModal({ gebruiker, onClose, onSave, token }) {
  const [form, setForm] = useState({
    calorieen:    gebruiker.doelen?.calorieen    || 2000,
    eiwitten:     gebruiker.doelen?.eiwitten     || 150,
    koolhydraten: gebruiker.doelen?.koolhydraten || 250,
    vetten:       gebruiker.doelen?.vetten       || 70,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${API}/api/admin/nutrition/${gebruiker.user_id}/doelen`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    setSaving(false);
    onSave(gebruiker.user_id, form);
  };

  const inp = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest">Voedingsdoelen</p>
            <h3 className="text-white font-bold">{gebruiker.naam}</h3>
          </div>
          <button onClick={onClose}><X size={16} className="text-zinc-500" /></button>
        </div>
        <div className="space-y-3">
          {Object.entries(KLEUR).map(([k, cfg]) => (
            <div key={k}>
              <label className="text-zinc-400 text-xs font-semibold block mb-1.5">{cfg.label}</label>
              <input type="number" value={form[k]} min={0}
                onChange={e => setForm(f => ({ ...f, [k]: +e.target.value }))}
                data-testid={`doel-${k}-input`}
                className={inp} />
            </div>
          ))}
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-doelen-btn"
          className="w-full mt-4 py-2.5 rounded-xl text-black font-bold text-sm disabled:opacity-40"
          style={{ background: "#C9A84C" }}>
          {saving ? "Opslaan..." : "Opslaan"}
        </button>
      </div>
    </div>
  );
}

export default function VoedingTab({ token }) {
  const [gebruikers, setGebruikers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [zoek,       setZoek]       = useState("");
  const [editUser,   setEditUser]   = useState(null);
  const [expanded,   setExpanded]   = useState(null);
  const [intakeUser, setIntakeUser] = useState(null);    // {user_id, naam, ...}
  const [intakeData, setIntakeData] = useState(null);    // bestaande intake
  const [intakeBerekening, setIntakeBerekening] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/admin/nutrition/overzicht`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const d = await r.json();
    setGebruikers(d.gebruikers || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = (userId, newDoelen) => {
    setGebruikers(prev => prev.map(g => g.user_id === userId ? { ...g, doelen: newDoelen } : g));
    setEditUser(null);
  };

  const openIntake = async (g) => {
    setIntakeLoading(true);
    setIntakeUser(g);
    setIntakeData(null);
    setIntakeBerekening(null);
    try {
      const r = await fetch(`${API}/api/admin/nutrition/${g.user_id}/intake`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setIntakeData(d.intake || null);
        setIntakeBerekening(d.berekening || null);
      }
    } catch {}
    setIntakeLoading(false);
  };

  const gefilterd = gebruikers.filter(g =>
    g.naam?.toLowerCase().includes(zoek.toLowerCase()) ||
    g.email?.toLowerCase().includes(zoek.toLowerCase())
  );

  // Statistieken bovenaan
  const actief = gebruikers.filter(g => g.logdagen > 0).length;
  const metDoelen = gebruikers.filter(g => g.doelen?.calorieen > 0).length;
  const gemCalorieen = gebruikers.filter(g => g.gem_week?.calorieen > 0).length > 0
    ? Math.round(gebruikers.filter(g => g.gem_week?.calorieen > 0).reduce((s, g) => s + g.gem_week.calorieen, 0)
        / gebruikers.filter(g => g.gem_week?.calorieen > 0).length) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-0.5">Beheer</p>
          <h2 className="text-xl font-bold text-white">Voeding</h2>
        </div>
      </div>

      {/* KPI stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: "Actieve loggers (7d)", val: actief, tot: gebruikers.length },
          { label: "Met doelen", val: metDoelen, tot: gebruikers.length },
          { label: "Gem. kcal/dag", val: gemCalorieen ? `${gemCalorieen} kcal` : "—" },
        ].map(({ label, val, tot }) => (
          <div key={label} className="bg-[#111] border border-white/8 rounded-2xl p-3 text-center">
            <p className="text-2xl font-black text-white tabular-nums">
              {typeof val === "string" ? val : val}{tot !== undefined && <span className="text-zinc-600 text-sm font-normal">/{tot}</span>}
            </p>
            <p className="text-zinc-600 text-[10px] font-semibold uppercase tracking-wide mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Zoek */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input value={zoek} onChange={e => setZoek(e.target.value)} placeholder="Zoek gebruiker..."
          data-testid="voeding-zoek-input"
          className="w-full bg-[#111] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40" />
      </div>

      {/* Tabel */}
      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {gefilterd.length === 0 && (
            <div className="bg-[#111] border border-white/6 rounded-2xl p-8 text-center">
              <p className="text-zinc-500 text-sm">Geen gebruikers gevonden</p>
            </div>
          )}
          {gefilterd.map(g => {
            const isOpen = expanded === g.user_id;
            const heeftLog = g.logdagen > 0;
            return (
              <div key={g.user_id} className="bg-[#111] border border-white/6 rounded-2xl overflow-hidden"
                data-testid={`voeding-row-${g.user_id}`}>
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/2 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : g.user_id)}>
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black text-black shrink-0"
                    style={{ background: "#C9A84C" }}>
                    {g.naam?.split(" ").map(n => n[0]).join("").slice(0,2).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{g.naam}</p>
                    <p className="text-zinc-500 text-xs truncate">{g.email}</p>
                  </div>
                  {/* Log badge */}
                  <div className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    heeftLog ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-zinc-600"
                  }`}>
                    {heeftLog ? `${g.logdagen}d actief` : "Geen logs"}
                  </div>
                  {/* Gem kcal */}
                  {g.gem_week?.calorieen > 0 && (
                    <div className="text-xs font-bold text-[#C9A84C] hidden sm:block tabular-nums">
                      {g.gem_week.calorieen} kcal
                    </div>
                  )}
                  {/* Doel knop */}
                  <button onClick={e => { e.stopPropagation(); setEditUser(g); }}
                    data-testid={`edit-doelen-${g.user_id}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors shrink-0">
                    <Target size={11} /> Doelen
                  </button>
                  {/* Intake knop */}
                  <button onClick={e => { e.stopPropagation(); openIntake(g); }}
                    data-testid={`edit-intake-${g.user_id}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl border border-[#C9A84C]/20 text-[#C9A84C]/70 hover:text-[#C9A84C] hover:border-[#C9A84C]/40 transition-colors shrink-0">
                    <Calculator size={11} /> Intake
                  </button>
                  {isOpen ? <ChevronUp size={14} className="text-zinc-600 shrink-0" /> : <ChevronDown size={14} className="text-zinc-600 shrink-0" />}
                </div>

                {/* Uitklapbaar detail */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-3">
                    <p className="text-zinc-500 text-xs font-bold uppercase tracking-wide mb-3">Gemiddelde afgelopen 7 dagen</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {Object.entries(KLEUR).map(([k, cfg]) => {
                        const Icon = cfg.icon;
                        const waarde = g.gem_week?.[k] || 0;
                        const doel = g.doelen?.[k] || 0;
                        return (
                          <div key={k} className="rounded-xl p-3"
                            style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <Icon size={11} style={{ color: cfg.text }} />
                              <span className="text-[10px] font-bold" style={{ color: cfg.text }}>{cfg.label}</span>
                            </div>
                            <p className="text-white font-black text-lg tabular-nums leading-none">{waarde}</p>
                            {doel > 0 && (
                              <>
                                <p className="text-zinc-600 text-[10px] mt-0.5">doel: {doel}</p>
                                <ProgressBar waarde={waarde} doel={doel} kleur={cfg.text} />
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Herinnering instellingen sectie (Super Admin) */}
      <ReminderInstellingen token={token} />

      {/* Doelen modal */}
      {editUser && (
        <DoelModal
          gebruiker={editUser}
          token={token}
          onClose={() => setEditUser(null)}
          onSave={handleSave}
        />
      )}

      {/* Intake modal */}
      {intakeUser && !intakeLoading && (
        <VoedingIntakeModal
          token={token}
          sporter={{ id: intakeUser.user_id, naam: intakeUser.naam, name: intakeUser.naam }}
          initialData={intakeData}
          initialBerekening={intakeBerekening}
          onClose={() => { setIntakeUser(null); setIntakeData(null); setIntakeBerekening(null); }}
          onSaved={(ber) => {
            setIntakeBerekening(ber);
            setIntakeUser(null);
            load();
          }}
          adminEndpoint={`/api/admin/nutrition/${intakeUser?.user_id}/intake`}
        />
      )}
    </div>
  );
}
